import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import limApi from "../../apis/limApi";
import {getDocument} from "./documentSlice";

export const getBoxDocuments = createAsyncThunk(
    'boxDocument/fetchAll',
    async (idBox, {rejectWithValue, dispatch}) => {
        try {
            const documents = await limApi.get(`/box/${idBox}/documents`).then(response => response.data.documents);

            if (documents.length) {
                for (const document of documents) {
                    await dispatch(getDocument(document.idDocument));
                }
            }

            return documents;
        } catch (e) {
            return rejectWithValue(e);
        }
    }
);

export const postBoxDocument = createAsyncThunk(
    'boxDocument/create',
    async ({idBox, documentBase64, values}, {rejectWithValue}) => {
        try {
            const response = await limApi.post(`/box/${idBox}/documents`,{
                idBox,
                document: documentBase64,
                documentDescription: values.fileTitle,
                documentName: values.name,
                documentFileName: values.name,
                documentSize: parseInt(values.size),
                displayPosition: values.displayPosition,
                fileType: values.ext
            });

            return response.data.document;
        } catch (e) {
            return rejectWithValue(e);
        }
    }
);

export const putBoxDocument = createAsyncThunk(
    'boxDocument/update',
    async ({idBox, idBoxDocument, documentBase64, values}, {rejectWithValue}) => {
        try {
            const response = await limApi.put(`/box/${idBox}/documents/${idBoxDocument}`,{
                idBox,
                idBoxDocument,
                idDocument: values.fileKey,
                document: documentBase64,
                documentDescription: values.fileTitle,
                documentName: values.name,
                documentFileName: values.name,
                documentSize: parseInt(values.size),
                displayPosition: values.displayPosition,
                fileType: values.ext
            });

            return response.data.document;
        } catch (e) {
            return rejectWithValue(e);
        }
    }
);

export const deleteBoxDocument = createAsyncThunk(
    "boxDocument/delete",
    async ({idBox, idBoxDocument}, {rejectWithValue}) => {
        try {
            await limApi.delete(`/box/${idBox}/documents/${idBoxDocument}`);
            return true;
        } catch (e) {
            return rejectWithValue(e);
        }
    }
);

const boxDocumentsSlice = createSlice({
    name: "boxDocument",
    initialState: {
        list: [],
        listLoading: undefined
    },
    reducers: {
        resetBoxDocuments: state => {
            state.list = [];
        }
    },
    extraReducers: builder => {
        builder
            .addCase(getBoxDocuments.pending, state => {
                state.listLoading = true;
            })
            .addCase(getBoxDocuments.fulfilled, (state, action) => {
                state.listLoading = undefined;
                state.list = action.payload;
            })
            .addCase(getBoxDocuments.rejected, state => {
                state.listLoading = undefined;
            })
    }
});

export const updateBoxDocuments = (idBox, documents) => async (dispatch, getState) => {
    const boxDocuments = getState().boxDocument.list;

    const newDocuments = documents.filter(doc => doc.isNew);
    const existingDocuments = documents.filter(doc => !doc.isNew && boxDocuments.length > 0);

    const newDocumentPromises = newDocuments.map(doc => {
        return dispatch(postBoxDocument({
            idBox,
            documentBase64: doc.base64.split(',')[1],
            values: doc
        }))
    });

    const existingDocumentPromise = existingDocuments.map(doc => {
        const documentDetails = boxDocuments.find(item => item.idDocument === doc.fileKey);
        if (documentDetails &&
            (
                documentDetails.displayPosition !== doc.displayPosition ||
                documentDetails.documentName !== doc.fileTitle ||
                documentDetails.documentFileName !== doc.name
            )
        ) {
            return dispatch(putBoxDocument({
                idBox,
                idBoxDocument: documentDetails.idBoxDocument,
                documentBase64: doc.base64.split(',')[1],
                values: doc
            }))
        }
    });

    const orderDocumentKeys = boxDocuments.map(value => value.idDocument);
    const fieldDocumentKeys = documents.map(value => value.fileKey);
    const deletedDocuments = orderDocumentKeys.filter(x => !fieldDocumentKeys.includes(x));

    const deletePromises = deletedDocuments.map(idDocument => {
        const deletedDocument = boxDocuments.find(item => item.idDocument === idDocument);
        if (deletedDocument) {
            return dispatch(deleteBoxDocument({idBox, idBoxDocument: deletedDocument.idBoxDocument}))
        }
    });

    const allPromises = [...newDocumentPromises, ...existingDocumentPromise, ...deletePromises].filter(prom => prom);

    if (allPromises.length) {
        await Promise.all(allPromises);
        await dispatch(getBoxDocuments(idBox));
    }
}

export const {resetBoxDocuments} = boxDocumentsSlice.actions;

export default boxDocumentsSlice.reducer;